import { useCallback, useEffect, useState } from 'react';
import { useMutes, useMuteScope } from './useMuteScope';
import { useStoreValue } from '@/lib/tinyStore';
import { notificationApi } from '../notificationsClient';
import {
  selectQuietHours, selectRules, selectSettings, settingsActions, settingsStore,
} from '../settingsStore';
import { desktopPermission, requestDesktopPermission } from '../desktopNotifications';
import type { NotificationSettings, NotificationRule, QuietHours } from '../types';

export { useMutes, useMuteScope };

export function useNotificationSettings(): NotificationSettings {
  return useStoreValue(settingsStore, selectSettings);
}

export function useQuietHours(): QuietHours {
  return useStoreValue(settingsStore, selectQuietHours);
}

export function useNotificationRules(): readonly NotificationRule[] {
  return useStoreValue(settingsStore, selectRules);
}

/** Loads persisted settings once. Mount in the runtime component only. */
export function useSettingsHydration(): void {
  useEffect(() => {
    let alive = true;
    void notificationApi
      .getSettings()
      .then((settings) => {
        if (alive) settingsActions.hydrate(settings);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);
}

/** Optimistic write-through: update the store, then persist. */
export function useSaveSettings(): (patch: Partial<NotificationSettings>) => void {
  return useCallback((patch) => {
    settingsActions.patch(patch);
    void notificationApi.saveSettings(patch).catch(() => undefined);
  }, []);
}

/** Desktop Notification API permission, with a gesture-safe request. */
export function useDesktopPermission(): {
  permission: NotificationPermission;
  request: () => void;
} {
  const [permission, setPermission] = useState<NotificationPermission>(desktopPermission);

  const request = useCallback(() => {
    void requestDesktopPermission().then(setPermission);
  }, []);

  return { permission, request };
}
